import React, { useState } from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import moment from 'moment';
import { MyCheckbox } from '../../../components/MyCheckbox';
import MyAppText from '../../../components/MyAppText';
import { cameraService } from '../../../services/central-api/cameras';
import MyDateRangePicker from '../../../components/MyDateRangePicker';
import { useForm } from 'react-hook-form';

interface ModalProps {
    isModalVisible: boolean;
    onClose: () => void;
}

interface ReportModalForm {
    period: { from: ValuePiece; to: ValuePiece; };
}

type ValuePiece = Date | null;
const regex = /^[a-zA-Z0-9 +\-./:=_ãõẽíáàâêóôúüçÃÕẼÍÁÀÂÊÓÔÚÜÇ]*$/;

export default function ReportModal({ onClose, isModalVisible }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const defaultEmptyValues: ReportModalForm = {
        period: { from: new Date(), to: new Date() },
    };

    const form = useForm<ReportModalForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const [isPdf, setIsPdf] = useState<boolean>(false);
    const [name, setName] = useState<string>('');

    function isNameInvalid(value: string) {
        return value == '' || !regex.test(value);
    }

    function isFormValid() {
        return !isNameInvalid(name);
    }

    function clearValues() {
        setName('');
        setIsPdf(false);
    }

    async function formSubmit() {
        try {
            const isValid = await form.trigger();

            if (!isValid) {
                throw new Error('invalid form');
            }

            const values = form.getValues();

            values.period.to!.setHours(23, 59, 59);
            values.period.from!.setHours(0, 0, 0);

            await cameraService.createReport({
                name: name,
                isPdf: isPdf,
                startDate: moment(values.period.from).format('YYYY-MM-DD'),
                endDate: moment(values.period.to).format('YYYY-MM-DD'),
            });

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            clearValues();
            onClose();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
                clearValues();
            }}
        >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '40%' }]}>
                    <MyAppText style={styles.textHeader}>{translate('offlineCamerasReport')}</MyAppText>
                    <FormInput
                        label={translate('name')}
                        placeholder={translate('name')}
                        value={name}
                        onChangeText={setName}
                        invalid={isNameInvalid}
                        errorMessage={translate('invalidCharacters')}
                    />
                    <MyDateRangePicker
                        label={translate('period')}
                        maxDate={moment().toDate()}
                        form={form}
                        fieldName='period'
                        zIndex={9}
                        required={true}
                    />
                    <MyCheckbox
                        style={styles.checkbox}
                        label={translate('PDF')}
                        checked={isPdf}
                        setChecked={(value) => setIsPdf(value)} />
                    <FormActions
                        onSubmit={async () => {
                            await formSubmit();
                        }}
                        onClose={() => {
                            onClose();
                            clearValues();
                        }}
                        disabled={!isFormValid()}
                        confirmText={'download'} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 15,
        maxHeight: 690
    },
    scrollContent: {
        rowGap: 15,
        paddingRight: 10
    },
    checkbox: {
        paddingHorizontal: 8,
        height: 40,
        fontSize: 16,
    },
    tagsLabel: {
        color: theme.colors.labelColor,
        fontSize: 16,
    },
    textHeader: {
        fontSize: 22,
        fontWeight: 'bold',
        color: theme.colors.textColor
    }
}));
